import { useQuery } from '@tanstack/react-query';

import { ApiError } from '../api/errors';

import type { CampaignRecord } from './queries';

// Plan 08-03 — campaign progress polling. Backend endpoint
// `GET /campaigns/:id/progress` is not in the OpenAPI schema; we issue raw
// fetch calls and mirror the `CampaignProgress` shape from
// `packages/backend/src/campaigns/progress.ts`.

const ORIGIN = typeof window === 'undefined' ? '' : window.location.origin;

export type CampaignItemStatusKey = 'pending' | 'snapshotted' | 'enqueued' | 'consumed' | 'skipped';

export type TradeStatusKey =
  | 'queued'
  | 'sending'
  | 'sent'
  | 'accepted'
  | 'declined'
  | 'expired'
  | 'cancelled'
  | 'failed';

export interface CampaignProgress {
  campaignId: string;
  status: CampaignRecord['status'];
  items: Record<CampaignItemStatusKey, number> & { total: number };
  trades: Record<TradeStatusKey, number> & { total: number };
  totals: { tradeLinks: number; unresolvedItems: number };
  timestamps: {
    launchedAt: string | null;
    completedAt: string | null;
    cancelledAt: string | null;
  };
}

const fetchProgress = async (id: string): Promise<CampaignProgress> => {
  const response = await fetch(`${ORIGIN}/api/campaigns/${id}/progress`, {
    method: 'GET',
    credentials: 'include',
  });
  if (!response.ok) {
    let code = 'progress_failed';
    try {
      const parsed = (await response.json()) as { error?: string };
      if (parsed.error) code = parsed.error;
    } catch {
      // ignore
    }
    throw new ApiError(response.status, code);
  }
  const body = (await response.json()) as { progress: CampaignProgress };
  return body.progress;
};

const isLive = (status: CampaignRecord['status'] | undefined): boolean =>
  status === 'running' || status === 'paused';

export const useCampaignProgress = (id: string, status: CampaignRecord['status'] | undefined) =>
  useQuery({
    queryKey: ['campaigns', 'one', id, 'progress'],
    queryFn: () => fetchProgress(id),
    refetchInterval: isLive(status) ? 5_000 : 30_000,
    refetchIntervalInBackground: false,
  });
